import { PercentPipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { UserDataService } from 'src/data-store/user-data.service';
import { IPieChartOptions } from 'src/models/charts.model';
import { IPieChartData } from 'src/services/sse/ai-investment-analysis.models';
import { CHART_COLOR_THEMES, IChartColorTheme } from 'src/util/charts.config';
import { escapeHtml } from 'src/util/functions';

interface IPieLegendItem {
  readonly name: string;
  readonly color: string;
}

@Component({
  selector: 'liberro-pie-chart',
  standalone: false,
  templateUrl: './liberro-pie-chart.component.html',
  styleUrl: './liberro-pie-chart.component.less'
})
export class LiberroPieChartComponent {
  public chartData = input.required<IPieChartData>();
  public noDataToDisplay = computed(() => !this.chartData() || !this.chartData().slices.length);
  private userDataService = inject(UserDataService);
  private percentPipe = inject(PercentPipe);
  protected readonly colors = computed<IChartColorTheme>(() =>
    CHART_COLOR_THEMES[this.userDataService.isDarkMode() ? 'DARK' : 'LIGHT'],
  );

  protected readonly legendItems = computed<IPieLegendItem[]>(() => {
    const palette = this.colors().palette;

    return this.chartData().slices.map((slice, index) => ({
      name: slice.name,
      color: palette[index % palette.length],
    }));
  });

  protected readonly chartOptions = computed<IPieChartOptions>(() => {
    const slices = this.chartData().slices;
    const colors = this.colors();

    return {
      series: slices.map(slice => slice.value),
      labels: slices.map(slice => escapeHtml(slice.name)),
      colors: [...colors.palette],
      chart: {
        type: 'donut',
        width: '100%',
        height: '100%',
        toolbar: { show: false },
      },
      plotOptions: {
        pie: {
          startAngle: 0,
          expandOnClick: false,
          donut: {
            size: '62%',
            labels: { show: false },
          },
          dataLabels: {
            offset: 0,
            minAngleToShowLabel: 10,
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (value: string, opts: { seriesIndex: number }) => this.percentPipe.transform(slices[opts.seriesIndex].value, '1.0-2', 'cs') ?? value,
        style: {
          fontSize: '11px',
          fontWeight: 700,
          colors: ['#F3F8F8'],
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 2,
          color: '#000000',
          opacity: 0.35,
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: [colors.surface],
      },
      legend: {
        show: false,
      },
      states: {
        active: { filter: { type: 'none' } },
      },
      tooltip: {
        fixed: {
          enabled: true,
          position: 'topLeft',
          offsetX: 0,
          offsetY: 0,
        },
        y: {
          formatter: (value: number) => this.percentPipe.transform(value, '1.0-2', 'cs') ?? value.toString(),
        },
      },
    };
  });
}
